import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HeroCodeReel
 *
 * STRUCT CODE LP ヒーローのルーレット演出。
 * - 4 文字コード (例: ACPU) と 5 軸スコア (000–1000) が、
 *   ルーレットのように回転して確定値に落ち着く
 * - その下にタイプ名（アーキタイプ）と一言を表示
 * - cycleMs ごとに次の type に切り替え、再ルーレット
 *
 * props:
 *   types         — 表示する type の配列 (getAllTypes 由来)
 *   cycleMs       — 1 サイクルの長さ (デフォルト 5500ms)
 *   reelMs        — ルーレット演出の長さ (デフォルト 1300ms)
 *   color         — 強調色 (デフォルト 黄金)
 */

const ROULETTE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 16px 8px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    gap: 14px;
    padding: 20px 8px 4px;
  }
`;

const TypeLabel = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.4em;
  color: ${p => p.theme.colors.textMuted};
  text-transform: uppercase;
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(8px, 1.6vw, 14px);
  font-family: ${p => p.theme.fonts.mono};
  font-weight: 600;
  font-size: clamp(1.6rem, 4vw, 2.6rem);
  letter-spacing: 0.18em;
  line-height: 1;
  color: ${p => p.$color};
  text-shadow: 0 0 14px ${p => p.$color}66, 0 0 28px ${p => p.$color}33;
`;

const CodeChar = styled.span`
  display: inline-block;
  min-width: 1ch;
  text-align: center;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: clamp(10px, 2vw, 22px);
  font-family: ${p => p.theme.fonts.mono};
  font-weight: 500;
  font-size: clamp(0.95rem, 1.8vw, 1.25rem);
  letter-spacing: 0.18em;
  color: ${p => p.theme.colors.text};
  opacity: 0.92;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    gap: 12px;
  }
`;

const ScoreCell = styled.span`
  display: inline-block;
  min-width: 3.2ch;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const Divider = styled.span`
  width: 32px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(230,199,126,0.4) 50%,
    transparent 100%
  );
  margin: 4px 0;
`;

const TypeName = styled(motion.div)`
  font-family: ${p => p.theme.fonts.display};
  font-style: italic;
  font-weight: 500;
  font-size: clamp(1.5rem, 3vw, 2rem);
  color: ${p => p.theme.colors.text};
  text-align: center;
  line-height: 1.3;
`;

const TypeNameSub = styled.span`
  font-family: ${p => p.theme.fonts.body};
  font-style: normal;
  font-size: 0.7em;
  color: ${p => p.theme.colors.textSecondary};
  margin-left: 0.5em;
  letter-spacing: 0.04em;
`;

const Tagline = styled(motion.p)`
  margin: 0;
  font-family: ${p => p.theme.fonts.body};
  font-size: clamp(0.95rem, 1.6vw, 1.1rem);
  line-height: 1.85;
  color: ${p => p.theme.colors.textSecondary};
  text-align: center;
  max-width: 38ch;
  min-height: 2em;
`;

const fadeText = {
  enter: { opacity: 0, y: 8 },
  show:  { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  exit:  { opacity: 0, y: -6, transition: { duration: 0.3 } },
};

// type code から決定的に 5軸スコア (0-1000) を生成 (StarChart と同じ seed 流派)
const seedFromCode = (code) => {
  let h = 0;
  for (let i = 0; i < code.length; i++) {
    h = (h * 31 + code.charCodeAt(i)) >>> 0;
  }
  return h || 1;
};

const mulberry32 = (seed) => {
  let t = seed;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const scoresFromCode = (code) => {
  const rng = mulberry32(seedFromCode(code || 'NONE'));
  return Array.from({ length: 5 }, () => Math.floor(rng() * 1001)); // 0-1000
};

const formatScore = (n) => String(n).padStart(3, '0');
const randomChar = () => ROULETTE_CHARS[Math.floor(Math.random() * ROULETTE_CHARS.length)];
const randomScore = () => Math.floor(Math.random() * 1000);

const HeroCodeReel = ({
  types,
  cycleMs = 5500,
  reelMs = 1300,
  color = '#E6C77E',
}) => {
  const safeTypes = useMemo(
    () => (Array.isArray(types) && types.length > 0 ? types : []),
    [types]
  );

  const [index, setIndex] = useState(0);
  const [displayCode, setDisplayCode] = useState(['T', 'Y', 'P', 'E']);
  const [displayScores, setDisplayScores] = useState([0, 0, 0, 0, 0]);
  const reelTimerRef = useRef(null);
  const cycleTimerRef = useRef(null);

  const current = safeTypes[index] || null;

  // ルーレットを回して、最終的にターゲットへ
  useEffect(() => {
    if (!current) return;
    const targetCode = (current.code || 'TYPE').slice(0, 4).padEnd(4, 'X').split('');
    const targetScores = scoresFromCode(current.code || 'TYPE');

    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(1, elapsed / reelMs);

      const nextCode = targetCode.map((ch, i) => {
        const local = Math.max(0, Math.min(1, (progress - i * 0.12) * 2.4));
        return local >= 1 ? ch : randomChar();
      });
      const nextScores = targetScores.map((target, i) => {
        const local = Math.max(0, Math.min(1, (progress - i * 0.08) * 2.4));
        return local >= 1 ? target : randomScore();
      });

      setDisplayCode(nextCode);
      setDisplayScores(nextScores);

      if (progress >= 1) {
        clearInterval(reelTimerRef.current);
        reelTimerRef.current = null;
      }
    };

    if (reelTimerRef.current) clearInterval(reelTimerRef.current);
    reelTimerRef.current = setInterval(tick, 60);
    tick();

    return () => {
      if (reelTimerRef.current) clearInterval(reelTimerRef.current);
      reelTimerRef.current = null;
    };
  }, [index, current, reelMs]);

  // cycleMs ごとに次の type へ
  useEffect(() => {
    if (safeTypes.length === 0) return;
    cycleTimerRef.current = setTimeout(() => {
      setIndex(prev => (prev + 1) % safeTypes.length);
    }, cycleMs);
    return () => {
      if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    };
  }, [index, cycleMs, safeTypes.length]);

  return (
    <Wrap>
      <TypeLabel>TYPE</TypeLabel>

      <CodeRow $color={color}>
        {displayCode.map((ch, i) => (
          <CodeChar key={i}>{ch}</CodeChar>
        ))}
      </CodeRow>

      <ScoreRow>
        {displayScores.map((s, i) => (
          <ScoreCell key={i}>{formatScore(s)}</ScoreCell>
        ))}
      </ScoreRow>

      <Divider />

      <AnimatePresence mode="wait">
        {current && (
          <motion.div
            key={`name-${current.code}`}
            initial="enter"
            animate="show"
            exit="exit"
            variants={fadeText}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
          >
            <TypeName>
              {current.name}
              {current.archetype && <TypeNameSub>（{current.archetype}）</TypeNameSub>}
            </TypeName>
            {current.tagline && <Tagline>{current.tagline}</Tagline>}
          </motion.div>
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default HeroCodeReel;
